import { graphql } from '@/__generated__';
import { SourceAppliedFilter } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetSourceListByUniqueId = graphql(/* GraphQL */ `
  query AppliedSources(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: SourceAppliedFilter
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        sources(first: $first, after: $after, filter: $filter) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              tags
              description
              meta
              database
              schema
              identifier
              sourceName
              name
              filePath
              patchPath
              healthIssues
              freshness {
                freshnessChecked
                freshnessStatus
                maxLoadedAt
                snapshottedAt
              }
            }
          }
        }
      }
    }
  }
`);

type SourceByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId: number | 'loading' | null;
  filter?: Partial<SourceAppliedFilter>;
  pause?: boolean;
};

export const useDiscoveryAppliedSources = ({
  first,
  after,
  environmentId,
  userId,
  filter,
  pause,
}: SourceByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetSourceListByUniqueId,
      variables: {
        first,
        after,
        environmentId,
        userId: userId as number | null,
        filter,
      },
      pause: pause || !environmentId || userId === 'loading',
    },
    (data) => {
      const sources = data.environment.applied?.sources;
      const pageInfo = sources?.pageInfo;
      if (sources == null || pageInfo == null) return undefined;
      return {
        totalCount: sources.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return (data.environment.applied?.sources.edges || [])
        .map((edge) => edge.node)
        .filter(truthy);
    },
    (data) => data.environment.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });

  return query;
};
